import React, { useState } from 'react';
import axios from 'axios';
import './index.css';
import { POST_GLOBAL_EXPANSION_DATA } from '../../api/globalExpansion';
import { uploadDocumentsToS3 } from '../../utils/s3Upload';
import {
    validateRequired,
    validateEmail,
    validateUrl,
    validateInteger,
    validateSelect,
    validateBoolean,
    validateDate,
} from '../../utils/validators';

const steps = [
    { id: 1, label: 'Company', percent: 20 },
    { id: 2, label: 'Markets', percent: 40 },
    { id: 3, label: 'Readiness', percent: 60 },
    { id: 4, label: 'Support', percent: 80 },
    { id: 5, label: 'Review', percent: 100 },
];

const GlobalExpansionPlanner = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submittingStatus, setSubmittingStatus] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [submittedData, setSubmittedData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showDataRoomInput, setShowDataRoomInput] = useState(false);
    const [tempDataRoomLink, setTempDataRoomLink] = useState('');
    const [showAddMarketInput, setShowAddMarketInput] = useState(false);
    const [newMarketInput, setNewMarketInput] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        // Step 1: Company Profile
        founderName: '',
        workEmail: '',
        companyName: '',
        website: '',
        headquarters: '',
        sector: '',
        stage: 'Growth',
        businessModel: '',
        currency: 'USD',
        homeMarketRevenue: '',
        homeEvidence: [],
        oneLineDesc: '',
        step1Confirmed: false,

        // Step 2: Target Markets
        priorityMarket: '',
        additionalMarkets: [],
        expansionObjective: '',
        desiredEntryTimeline: '',
        whyThisMarket: [],
        targetCustomer: '',
        entryModel: '',
        evidenceSignals: '',
        marketsToExclude: '',

        // Step 3: Readiness Check
        expansionOwner: '',
        budgetCurrency: 'USD',
        availableBudget: '',
        runwayMonths: '',
        leadershipCapacity: '',
        preparationCompleted: [],
        productLocalisation: '',
        localEntityRequired: '',
        capabilities: {
            sales: '',
            marketing: '',
            customerSuccess: '',
            operations: '',
            compliance: '',
        },
        biggestRisk: '',
        nonNegotiables: '',

        // Step 4: Support Requirements
        supportWorkstreams: [],
        engagementFormat: 'Entry sprint (12-16 weeks)',
        desiredStartDate: '',
        primaryContact: 'Founder / Expansion lead',
        decisionSpeed: 'Fast',
        introductionsNeeded: [],
        first90DayOutcome: '',
        anythingElse: '',
        supportAcknowledged: false,

        // Step 5: Review Declarations & Documents
        documents: [],
        dataRoomLink: '',
        declarationAccuracy: false,
        declarationNoGuarantee: false,
    });

    const handleChange = (field, value) => {
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
        if (errorMsg) setErrorMsg('');
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCapabilityChange = (cap, val) => {
        setFormData((prev) => ({
            ...prev,
            capabilities: { ...prev.capabilities, [cap]: val },
        }));
    };

    const toggleArrayItem = (field, item) => {
        setFormData((prev) => {
            const exists = prev[field].includes(item);
            return {
                ...prev,
                [field]: exists
                    ? prev[field].filter((i) => i !== item)
                    : [...prev[field], item],
            };
        });
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
        if (errorMsg) setErrorMsg('');
    };

    const handleFileSelection = (e, docType = 'Market Research') => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setErrorMsg('');

        const newSelectedDocs = Array.from(files).map((file) => ({
            name: file.name,
            size: (file.size / 1024).toFixed(1) + ' KB',
            docType: docType,
            rawFile: file,
            isPending: true,
        }));

        setFormData((prev) => ({
            ...prev,
            documents: [...(prev.documents || []), ...newSelectedDocs],
        }));

        if (e.target) e.target.value = '';
    };

    const handleRemoveDoc = (index) => {
        setFormData((prev) => ({
            ...prev,
            documents: (prev.documents || []).filter((_, i) => i !== index),
        }));
    };

    const handleAddCustomMarket = () => {
        const trimmed = newMarketInput.trim();
        if (trimmed && !formData.additionalMarkets.includes(trimmed)) {
            setFormData((prev) => ({
                ...prev,
                additionalMarkets: [...prev.additionalMarkets, trimmed],
            }));
            setNewMarketInput('');
            setShowAddMarketInput(false);
        }
    };

    const handleRemoveMarket = (mkt) => {
        setFormData((prev) => ({
            ...prev,
            additionalMarkets: prev.additionalMarkets.filter((m) => m !== mkt),
        }));
    };

    // Strict Validation on Continue Click using centralized utils/validators
    const validateCurrentStep = (step) => {
        const errs = {};

        if (step === 1) {
            const nameErr = validateRequired(formData.founderName, 'Founder or lead name', 2);
            if (nameErr) errs.founderName = nameErr;

            const emailErr = validateEmail(formData.workEmail, 'Work email', true);
            if (emailErr) errs.workEmail = emailErr;

            const companyErr = validateRequired(formData.companyName, 'Company name', 2);
            if (companyErr) errs.companyName = companyErr;

            if (formData.website && formData.website.trim()) {
                const urlErr = validateUrl(formData.website, 'Company website', false);
                if (urlErr) errs.website = urlErr;
            }

            const hqErr = validateRequired(formData.headquarters, 'Headquarters', 2);
            if (hqErr) errs.headquarters = hqErr;

            const sectorErr = validateSelect(formData.sector, 'a sector');
            if (sectorErr) errs.sector = sectorErr;

            const revErr = validateInteger(formData.homeMarketRevenue, 'Home-market revenue', true);
            if (revErr) {
                errs.homeMarketRevenue = revErr;
            } else if (parseInt(formData.homeMarketRevenue, 10) <= 0) {
                errs.homeMarketRevenue = 'Home-market revenue must be greater than 0.';
            }

            const descErr = validateRequired(formData.oneLineDesc, 'One-line company description', 5);
            if (descErr) errs.oneLineDesc = descErr;

            const confirmErr = validateBoolean(formData.step1Confirmed, 'Please confirm that the information shared is accurate.');
            if (confirmErr) errs.step1Confirmed = confirmErr;
        }

        if (step === 2) {
            const marketErr = validateSelect(formData.priorityMarket, 'a priority market');
            if (marketErr) errs.priorityMarket = marketErr;

            const objErr = validateSelect(formData.expansionObjective, 'an expansion objective');
            if (objErr) errs.expansionObjective = objErr;

            const timelineErr = validateSelect(formData.desiredEntryTimeline, 'a desired entry timeline');
            if (timelineErr) errs.desiredEntryTimeline = timelineErr;

            const customerErr = validateSelect(formData.targetCustomer, 'a target customer segment');
            if (customerErr) errs.targetCustomer = customerErr;

            const modelErr = validateSelect(formData.entryModel, 'an entry model');
            if (modelErr) errs.entryModel = modelErr;

            const signalsErr = validateRequired(formData.evidenceSignals, 'Evidence or signals', 5);
            if (signalsErr) errs.evidenceSignals = signalsErr;
        }

        if (step === 3) {
            const ownerErr = validateSelect(formData.expansionOwner, 'an expansion owner');
            if (ownerErr) errs.expansionOwner = ownerErr;

            const budgetErr = validateInteger(formData.availableBudget, 'Available entry budget', true);
            if (budgetErr) {
                errs.availableBudget = budgetErr;
            } else if (parseInt(formData.availableBudget, 10) <= 0) {
                errs.availableBudget = 'Available entry budget must be greater than 0.';
            }

            const runwayErr = validateSelect(formData.runwayMonths, 'current operating runway');
            if (runwayErr) errs.runwayMonths = runwayErr;

            const capacityErr = validateSelect(formData.leadershipCapacity, 'leadership capacity');
            if (capacityErr) errs.leadershipCapacity = capacityErr;

            const riskErr = validateRequired(formData.biggestRisk, 'Biggest expansion risk', 5);
            if (riskErr) errs.biggestRisk = riskErr;
        }

        if (step === 4) {
            if (!formData.supportWorkstreams || formData.supportWorkstreams.length === 0) {
                errs.supportWorkstreams = 'Please select at least one support workstream.';
            }

            const dateErr = validateDate(formData.desiredStartDate, 'Desired start date', true);
            if (dateErr) errs.desiredStartDate = dateErr;

            const outcomeErr = validateRequired(formData.first90DayOutcome, 'First 90-day outcome', 5);
            if (outcomeErr) errs.first90DayOutcome = outcomeErr;

            const ackErr = validateBoolean(formData.supportAcknowledged, 'Please accept the support terms before continuing.');
            if (ackErr) errs.supportAcknowledged = ackErr;
        }

        if (step === 5) {
            if (formData.dataRoomLink && formData.dataRoomLink.trim()) {
                const drErr = validateUrl(formData.dataRoomLink, 'Data room link', false);
                if (drErr) errs.dataRoomLink = drErr;
            }
            const accErr = validateBoolean(formData.declarationAccuracy, 'You must confirm accuracy before submitting.');
            const guarErr = validateBoolean(formData.declarationNoGuarantee, 'You must acknowledge the disclaimer before submitting.');
            if (accErr || guarErr) {
                errs.declarations = 'Both declarations must be acknowledged before submitting.';
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleNext = async (e) => {
        if (e) e.preventDefault();
        setErrorMsg('');

        if (currentStep < 5) {
            if (validateCurrentStep(currentStep)) {
                setCurrentStep((prev) => prev + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            return;
        }

        // Final step validation & submission with S3 bucket upload
        if (!validateCurrentStep(5)) {
            setErrorMsg('Please confirm all declarations before submitting your expansion plan.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setLoading(true);
        try {
            // 1. Upload any pending documents to AWS S3
            let finalDocuments = [];
            if (formData.documents && formData.documents.length > 0) {
                setSubmittingStatus('Uploading documents to secure storage...');
                finalDocuments = await uploadDocumentsToS3(formData.documents, 'globalexpansion');
            }

            // 2. Submit expansion plan payload
            setSubmittingStatus('Submitting your expansion plan...');
            const payload = {
                ...formData,
                documents: finalDocuments,
            };

            const response = await axios.post(POST_GLOBAL_EXPANSION_DATA, payload);
            if (response.status === 201 || response.status === 200) {
                const savedData = response.data?.data || payload;
                setSubmittedData(savedData);
                setCurrentStep(6);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Error submitting Global Expansion plan:', error);
            if (error.response && error.response.data) {
                const details = error.response.data.details;
                if (Array.isArray(details)) {
                    setErrorMsg(details.join('. '));
                } else if (typeof details === 'string') {
                    setErrorMsg(details);
                } else if (error.response.data.error) {
                    setErrorMsg(error.response.data.error);
                } else {
                    setErrorMsg('An error occurred while submitting your expansion plan. Please try again.');
                }
            } else {
                setErrorMsg(error.message || 'Network error. Please check your connection and try again.');
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setLoading(false);
            setSubmittingStatus('');
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const currentPercent = currentStep <= 5 ? steps[currentStep - 1].percent : 100;

    return (
        <div className="gep-page">
            {/* 1. Page Subheader & Dynamic Stepper Header */}
            {currentStep <= 5 && (
                <section className="gep-header-section">
                    <div className="gep-header-top">
                        <span className="gep-tag">GLOBAL EXPANSION PLANNER</span>
                        <h1 className="gep-main-title">
                            {currentStep === 1 && 'Build your market plan.'}
                            {currentStep === 2 && 'Choose where to grow.'}
                            {currentStep === 3 && 'Test your readiness.'}
                            {currentStep === 4 && 'Design the right support.'}
                            {currentStep === 5 && 'Review your expansion plan.'}
                        </h1>
                        <p className="gep-main-desc">
                            {currentStep === 1 && "Start with your current position. We'll use your goals, evidence and constraints to shape a practical market-entry path."}
                            {currentStep === 2 && "Identify the markets you are considering and the evidence behind each opportunity."}
                            {currentStep === 3 && "Assess whether your team, resources and operating model can support a disciplined market entry."}
                            {currentStep === 4 && "Select the expertise, introductions and execution support needed to move from market interest to first traction."}
                            {currentStep === 5 && "Confirm the market thesis, readiness gaps and support priorities before submitting for review."}
                        </p>
                    </div>

                    {/* Stepper Progress Bar */}
                    <div className="gep-stepper-wrapper">
                        <div className="gep-stepper-track">
                            <div
                                className="gep-stepper-fill"
                                style={{ width: `${currentPercent}%` }}
                            ></div>
                            {steps.map((s) => {
                                const isActive = currentStep === s.id;
                                const isCompleted = currentStep > s.id;
                                return (
                                    <div
                                        key={s.id}
                                        className={`gep-step-node ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                        onClick={() => s.id < currentStep && setCurrentStep(s.id)}
                                    >
                                        <div className="gep-node-circle">
                                            {isCompleted ? '✓' : `0${s.id}`}
                                        </div>
                                        <span className="gep-node-name">{s.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="gep-step-percentage-label">{currentPercent}% complete</div>
                    </div>
                </section>
            )}

            {/* 3. Main Form Split Container */}
            <main className="gep-main-container">
                {/* =========================================================================
            STEPS 1 TO 5 FORM SECTION
            ========================================================================= */}
                {currentStep <= 5 && (
                    <section className="gep-form-card">
                        <form onSubmit={handleNext}>
                            {/* STEP 1: COMPANY PROFILE */}
                            {currentStep === 1 && (
                                <div className="gep-step-body">
                                    <div className="gep-step-title-group">
                                        <h2>Company profile</h2>
                                        <p>Tell us what is proven before we plan what comes next</p>
                                    </div>

                                    <div className="gep-grid-2">
                                        <div className="gep-field">
                                            <label>Founder or lead name</label>
                                            <input
                                                type="text"
                                                placeholder="Your full name"
                                                value={formData.founderName}
                                                onChange={(e) => handleChange('founderName', e.target.value)}
                                            />
                                            {errors.founderName && <span className="gep-err">{errors.founderName}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Work email</label>
                                            <input
                                                type="email"
                                                placeholder="you@company.com"
                                                value={formData.workEmail}
                                                onChange={(e) => handleChange('workEmail', e.target.value)}
                                            />
                                            {errors.workEmail && <span className="gep-err">{errors.workEmail}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Company name</label>
                                            <input
                                                type="text"
                                                placeholder="Registered or operating name"
                                                value={formData.companyName}
                                                onChange={(e) => handleChange('companyName', e.target.value)}
                                            />
                                            {errors.companyName && <span className="gep-err">{errors.companyName}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Company website</label>
                                            <input
                                                type="text"
                                                placeholder="https://"
                                                value={formData.website}
                                                onChange={(e) => handleChange('website', e.target.value)}
                                            />
                                            {errors.website && <span className="gep-err">{errors.website}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Headquarters</label>
                                            <input
                                                type="text"
                                                placeholder="City, Country"
                                                value={formData.headquarters}
                                                onChange={(e) => handleChange('headquarters', e.target.value)}
                                            />
                                            {errors.headquarters && <span className="gep-err">{errors.headquarters}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Sector</label>
                                            <select
                                                value={formData.sector}
                                                onChange={(e) => handleChange('sector', e.target.value)}
                                            >
                                                <option value="">Select sector</option>
                                                <option value="B2B SaaS">B2B SaaS</option>
                                                <option value="Fintech">Fintech</option>
                                                <option value="Healthtech">Healthtech</option>
                                                <option value="Consumer / Commerce">Consumer / Commerce</option>
                                                <option value="Enterprise Software">Enterprise Software</option>
                                            </select>
                                            {errors.sector && <span className="gep-err">{errors.sector}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Current company stage</label>
                                            <div className="gep-stage-buttons">
                                                {['Pre-revenue', 'Early revenue', 'Growth', 'Scale', 'Enterprise'].map((stg) => (
                                                    <button
                                                        type="button"
                                                        key={stg}
                                                        className={`gep-stage-btn ${formData.stage === stg ? 'selected' : ''}`}
                                                        onClick={() => handleChange('stage', stg)}
                                                    >
                                                        <span>📊</span>
                                                        <span>{stg}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field">
                                            <label>Primary business model</label>
                                            <select
                                                value={formData.businessModel}
                                                onChange={(e) => handleChange('businessModel', e.target.value)}
                                            >
                                                <option value="">Select business model</option>
                                                <option value="Subscription / SaaS">Subscription / SaaS</option>
                                                <option value="Transactional">Transactional</option>
                                                <option value="Marketplace">Marketplace</option>
                                                <option value="Enterprise License">Enterprise License</option>
                                            </select>
                                        </div>

                                        <div className="gep-field">
                                            <label>Home-market revenue (Integer amount)</label>
                                            <div className="gep-split-input">
                                                <select
                                                    value={formData.currency}
                                                    onChange={(e) => handleChange('currency', e.target.value)}
                                                    className="gep-currency-select"
                                                >
                                                    <option value="USD">USD</option>
                                                    <option value="EUR">EUR</option>
                                                    <option value="GBP">GBP</option>
                                                    <option value="SGD">SGD</option>
                                                </select>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. 3500000"
                                                    value={formData.homeMarketRevenue}
                                                    onChange={(e) => handleChange('homeMarketRevenue', e.target.value)}
                                                />
                                            </div>
                                            {errors.homeMarketRevenue && <span className="gep-err">{errors.homeMarketRevenue}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Evidence from your home market</label>
                                            <div className="gep-checkboxes-grid">
                                                {['Repeat customers', 'Recurring revenue', 'Enterprise contracts', 'Channel partners', 'Strong retention', 'Regulatory approval', 'Proprietary technology'].map((item) => (
                                                    <label key={item} className="gep-checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.homeEvidence.includes(item)}
                                                            onChange={() => toggleArrayItem('homeEvidence', item)}
                                                        />
                                                        <span>{item}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>One-line company description</label>
                                            <textarea
                                                rows={3}
                                                placeholder="What do you offer, for whom, and what traction have you proven?"
                                                value={formData.oneLineDesc}
                                                onChange={(e) => handleChange('oneLineDesc', e.target.value)}
                                            ></textarea>
                                            {errors.oneLineDesc && <span className="gep-err">{errors.oneLineDesc}</span>}
                                        </div>
                                    </div>

                                    <div className="gep-checkbox-row">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.step1Confirmed}
                                                onChange={(e) => handleChange('step1Confirmed', e.target.checked)}
                                            />
                                            <span>I confirm that the information shared is accurate and may be reviewed by the Markoknow Global Expansion team.</span>
                                        </label>
                                        {errors.step1Confirmed && <span className="gep-err">{errors.step1Confirmed}</span>}
                                    </div>
                                </div>
                            )}

                            {/* STEP 2: TARGET MARKETS */}
                            {currentStep === 2 && (
                                <div className="gep-step-body">
                                    <div className="gep-step-title-group">
                                        <h2>Target markets</h2>
                                        <p>Tell us where you want to expand and why</p>
                                    </div>

                                    <div className="gep-grid-2">
                                        <div className="gep-field full-width">
                                            <label>Priority market</label>
                                            <select
                                                value={formData.priorityMarket}
                                                onChange={(e) => handleChange('priorityMarket', e.target.value)}
                                            >
                                                <option value="">Select a country or region</option>
                                                <option value="United Arab Emirates">United Arab Emirates</option>
                                                <option value="Singapore & SEA">Singapore & SEA</option>
                                                <option value="United Kingdom">United Kingdom</option>
                                                <option value="United States">United States</option>
                                                <option value="India">India</option>
                                            </select>
                                            {errors.priorityMarket && <span className="gep-err">{errors.priorityMarket}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Additional markets under consideration</label>
                                            <div className="gep-add-markets-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                {formData.additionalMarkets && formData.additionalMarkets.map((mkt) => (
                                                    <span key={mkt} className="gep-doc-chip" style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#5c4eb4' }}>
                                                        🌐 {mkt}
                                                        <button
                                                            type="button"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', marginLeft: 4, fontWeight: 'bold' }}
                                                            onClick={() => handleRemoveMarket(mkt)}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}

                                                {['Germany', 'Australia', 'Saudi Arabia', 'Japan', 'Canada'].filter(m => !formData.additionalMarkets.includes(m) && formData.priorityMarket !== m).slice(0, 3).map((suggested) => (
                                                    <button
                                                        key={suggested}
                                                        type="button"
                                                        className="gep-add-market-btn"
                                                        onClick={() => setFormData(prev => ({ ...prev, additionalMarkets: [...prev.additionalMarkets, suggested] }))}
                                                    >
                                                        + {suggested}
                                                    </button>
                                                ))}

                                                {!showAddMarketInput ? (
                                                    <button
                                                        type="button"
                                                        className="gep-add-market-btn"
                                                        onClick={() => setShowAddMarketInput(true)}
                                                    >
                                                        + Custom market
                                                    </button>
                                                ) : (
                                                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Enter country or region"
                                                            value={newMarketInput}
                                                            onChange={(e) => setNewMarketInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleAddCustomMarket();
                                                                }
                                                            }}
                                                            style={{ padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6 }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="gep-btn-signup"
                                                            style={{ padding: '6px 12px', fontSize: 11 }}
                                                            onClick={handleAddCustomMarket}
                                                        >
                                                            Add
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="gep-btn-link"
                                                            style={{ fontSize: 11 }}
                                                            onClick={() => {
                                                                setNewMarketInput('');
                                                                setShowAddMarketInput(false);
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="gep-field">
                                            <label>Primary expansion objective</label>
                                            <select
                                                value={formData.expansionObjective}
                                                onChange={(e) => handleChange('expansionObjective', e.target.value)}
                                            >
                                                <option value="">Select objective</option>
                                                <option value="Revenue growth">Revenue growth</option>
                                                <option value="Partnerships">Partnerships</option>
                                                <option value="Operations">Operations</option>
                                                <option value="Talent">Talent</option>
                                            </select>
                                            {errors.expansionObjective && <span className="gep-err">{errors.expansionObjective}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Desired entry timeline</label>
                                            <select
                                                value={formData.desiredEntryTimeline}
                                                onChange={(e) => handleChange('desiredEntryTimeline', e.target.value)}
                                            >
                                                <option value="">Select timeline</option>
                                                <option value="0–3 months">0–3 months</option>
                                                <option value="3–6 months">3–6 months</option>
                                                <option value="6–12 months">6–12 months</option>
                                                <option value="Exploring">Exploring</option>
                                            </select>
                                            {errors.desiredEntryTimeline && <span className="gep-err">{errors.desiredEntryTimeline}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Why this market?</label>
                                            <div className="gep-checkboxes-grid">
                                                {['Existing customer demand', 'Inbound enquiries', 'Partner opportunity', 'Market size', 'Favourable regulation', 'Lower competition', 'Regional hub', 'Founder network'].map((reason) => (
                                                    <label key={reason} className="gep-checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.whyThisMarket.includes(reason)}
                                                            onChange={() => toggleArrayItem('whyThisMarket', reason)}
                                                        />
                                                        <span>{reason}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field">
                                            <label>Target customer</label>
                                            <select
                                                value={formData.targetCustomer}
                                                onChange={(e) => handleChange('targetCustomer', e.target.value)}
                                            >
                                                <option value="">Select segment</option>
                                                <option value="Enterprise">Enterprise</option>
                                                <option value="SME">SME</option>
                                                <option value="Consumer">Consumer</option>
                                                <option value="Government">Government</option>
                                            </select>
                                            {errors.targetCustomer && <span className="gep-err">{errors.targetCustomer}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Entry model being considered</label>
                                            <select
                                                value={formData.entryModel}
                                                onChange={(e) => handleChange('entryModel', e.target.value)}
                                            >
                                                <option value="">Select model</option>
                                                <option value="Direct sales">Direct sales</option>
                                                <option value="Partner-led">Partner-led</option>
                                                <option value="Entity setup">Entity setup</option>
                                                <option value="Marketplace">Marketplace</option>
                                            </select>
                                            {errors.entryModel && <span className="gep-err">{errors.entryModel}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Evidence or signals</label>
                                            <textarea
                                                rows={3}
                                                placeholder="Share customer requests, market research, pilots, partnerships or other signals supporting this market."
                                                value={formData.evidenceSignals}
                                                onChange={(e) => handleChange('evidenceSignals', e.target.value)}
                                            ></textarea>
                                            {errors.evidenceSignals && <span className="gep-err">{errors.evidenceSignals}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Markets to exclude or avoid</label>
                                            <textarea
                                                rows={2}
                                                placeholder="List countries, regions, conflicts or constraints that should not be considered."
                                                value={formData.marketsToExclude}
                                                onChange={(e) => handleChange('marketsToExclude', e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 3: READINESS CHECK */}
                            {currentStep === 3 && (
                                <div className="gep-step-body">
                                    <div className="gep-step-title-group">
                                        <h2>Expansion readiness</h2>
                                        <p>Identify what is ready, what is missing and what could slow execution</p>
                                    </div>

                                    <div className="gep-grid-2">
                                        <div className="gep-field">
                                            <label>Expansion owner</label>
                                            <select
                                                value={formData.expansionOwner}
                                                onChange={(e) => handleChange('expansionOwner', e.target.value)}
                                            >
                                                <option value="">Select owner</option>
                                                <option value="Founder / Leadership team">Founder / Leadership team</option>
                                                <option value="Dedicated lead">Dedicated lead</option>
                                                <option value="Not assigned">Not assigned</option>
                                            </select>
                                            {errors.expansionOwner && <span className="gep-err">{errors.expansionOwner}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Available entry budget (Integer amount)</label>
                                            <div className="gep-split-input">
                                                <span className="gep-input-prefix">USD</span>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. 250000"
                                                    value={formData.availableBudget}
                                                    onChange={(e) => handleChange('availableBudget', e.target.value)}
                                                />
                                            </div>
                                            {errors.availableBudget && <span className="gep-err">{errors.availableBudget}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Current operating runway</label>
                                            <select
                                                value={formData.runwayMonths}
                                                onChange={(e) => handleChange('runwayMonths', e.target.value)}
                                            >
                                                <option value="">Select runway</option>
                                                <option value="< 6 months">&lt; 6 months</option>
                                                <option value="6 - 12 months">6 – 12 months</option>
                                                <option value="12 - 18 months">12 – 18 months</option>
                                                <option value="18+ months">18+ months</option>
                                            </select>
                                            {errors.runwayMonths && <span className="gep-err">{errors.runwayMonths}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Leadership capacity</label>
                                            <select
                                                value={formData.leadershipCapacity}
                                                onChange={(e) => handleChange('leadershipCapacity', e.target.value)}
                                            >
                                                <option value="">Select capacity</option>
                                                <option value="High">High</option>
                                                <option value="Moderate">Moderate</option>
                                                <option value="Limited">Limited</option>
                                                <option value="Unsure">Unsure</option>
                                            </select>
                                            {errors.leadershipCapacity && <span className="gep-err">{errors.leadershipCapacity}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Preparation completed</label>
                                            <div className="gep-checkboxes-grid">
                                                {['Market research', 'Customer interviews', 'Competitor mapping', 'Pricing validation', 'Legal review', 'Tax review', 'Localisation plan', 'Partner outreach'].map((prep) => (
                                                    <label key={prep} className="gep-checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.preparationCompleted.includes(prep)}
                                                            onChange={() => toggleArrayItem('preparationCompleted', prep)}
                                                        />
                                                        <span>{prep}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field">
                                            <label>Product localisation required</label>
                                            <div className="gep-radios-inline">
                                                {['None', 'Light', 'Significant'].map((opt) => (
                                                    <label key={opt}>
                                                        <input
                                                            type="radio"
                                                            name="prodLoc"
                                                            checked={formData.productLocalisation === opt}
                                                            onChange={() => handleChange('productLocalisation', opt)}
                                                        />
                                                        <span>{opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field">
                                            <label>Local entity required</label>
                                            <div className="gep-radios-inline">
                                                {['Yes', 'No', 'Unsure'].map((opt) => (
                                                    <label key={opt}>
                                                        <input
                                                            type="radio"
                                                            name="locEnt"
                                                            checked={formData.localEntityRequired === opt}
                                                            onChange={() => handleChange('localEntityRequired', opt)}
                                                        />
                                                        <span>{opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Internal capabilities matrix */}
                                        <div className="gep-field full-width">
                                            <label>Internal capabilities</label>
                                            <div className="gep-capabilities-table">
                                                {[
                                                    { key: 'sales', label: 'Sales' },
                                                    { key: 'marketing', label: 'Marketing' },
                                                    { key: 'customerSuccess', label: 'Customer success' },
                                                    { key: 'operations', label: 'Operations' },
                                                    { key: 'compliance', label: 'Compliance' },
                                                ].map((cap) => (
                                                    <div key={cap.key} className="gep-cap-row">
                                                        <span className="gep-cap-label">{cap.label}</span>
                                                        <div className="gep-cap-options">
                                                            {['Ready', 'Partial', 'Gap'].map((status) => (
                                                                <label key={status}>
                                                                    <input
                                                                        type="radio"
                                                                        name={`cap_${cap.key}`}
                                                                        checked={formData.capabilities[cap.key] === status}
                                                                        onChange={() => handleCapabilityChange(cap.key, status)}
                                                                    />
                                                                    <span>{status}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Biggest expansion risk</label>
                                            <textarea
                                                rows={2}
                                                placeholder="What could prevent this market entry from succeeding?"
                                                value={formData.biggestRisk}
                                                onChange={(e) => handleChange('biggestRisk', e.target.value)}
                                            ></textarea>
                                            {errors.biggestRisk && <span className="gep-err">{errors.biggestRisk}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Non-negotiable constraints</label>
                                            <textarea
                                                rows={2}
                                                placeholder="Share budget limits, deadlines, regulations, team constraints or dependencies."
                                                value={formData.nonNegotiables}
                                                onChange={(e) => handleChange('nonNegotiables', e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 4: DESIGN SUPPORT */}
                            {currentStep === 4 && (
                                <div className="gep-step-body">
                                    <div className="gep-step-title-group">
                                        <h2>Support requirements</h2>
                                        <p>Choose the workstreams that would create the most value</p>
                                    </div>

                                    <div className="gep-support-cards-grid">
                                        {[
                                            { id: 'Market intelligence', icon: '📊', title: 'Market intelligence', sub: 'Demand, competition and entry economics' },
                                            { id: 'Customer validation', icon: '💬', title: 'Customer validation', sub: 'Interviews, pilots and pricing feedback' },
                                            { id: 'Local operators', icon: '👥', title: 'Local operators', sub: 'Vetted specialists with market experience' },
                                            { id: 'Partnership development', icon: '🤝', title: 'Partnership development', sub: 'Channels, distributors and strategic partners' },
                                            { id: 'Legal & compliance network', icon: '📑', title: 'Legal & compliance network', sub: 'Relevant independent professional referrals' },
                                            { id: 'Launch execution', icon: '🚀', title: 'Launch execution', sub: 'Milestones, local activation and first traction' },
                                        ].map((card) => {
                                            const isSelected = formData.supportWorkstreams.includes(card.id);
                                            return (
                                                <div
                                                    key={card.id}
                                                    className={`gep-support-card ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => toggleArrayItem('supportWorkstreams', card.id)}
                                                >
                                                    <div className="gep-sc-head">
                                                        <span className="gep-sc-icon">{card.icon}</span>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => { }}
                                                        />
                                                    </div>
                                                    <h4>{card.title}</h4>
                                                    <p>{card.sub}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {errors.supportWorkstreams && <span className="gep-err">{errors.supportWorkstreams}</span>}

                                    <div className="gep-grid-2" style={{ marginTop: '24px' }}>
                                        <div className="gep-field">
                                            <label>Preferred engagement format</label>
                                            <select
                                                value={formData.engagementFormat}
                                                onChange={(e) => handleChange('engagementFormat', e.target.value)}
                                            >
                                                <option value="Market assessment">Market assessment</option>
                                                <option value="Entry sprint (12-16 weeks)">Entry sprint (12-16 weeks)</option>
                                                <option value="Ongoing support">Ongoing support</option>
                                            </select>
                                        </div>

                                        <div className="gep-field">
                                            <label>Desired start date (Format: MM / YYYY)</label>
                                            <input
                                                type="text"
                                                placeholder="MM / YYYY"
                                                value={formData.desiredStartDate}
                                                onChange={(e) => handleChange('desiredStartDate', e.target.value)}
                                            />
                                            {errors.desiredStartDate && <span className="gep-err">{errors.desiredStartDate}</span>}
                                        </div>

                                        <div className="gep-field">
                                            <label>Primary internal contact</label>
                                            <select
                                                value={formData.primaryContact}
                                                onChange={(e) => handleChange('primaryContact', e.target.value)}
                                            >
                                                <option value="Founder / Expansion lead">Founder / Expansion lead</option>
                                                <option value="Commercial lead">Commercial lead</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>

                                        <div className="gep-field">
                                            <label>Decision-making speed</label>
                                            <select
                                                value={formData.decisionSpeed}
                                                onChange={(e) => handleChange('decisionSpeed', e.target.value)}
                                            >
                                                <option value="Fast">Fast</option>
                                                <option value="Standard">Standard</option>
                                                <option value="Exploratory">Exploratory</option>
                                            </select>
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Introductions that matter most</label>
                                            <div className="gep-checkboxes-grid">
                                                {['Potential customers', 'Channel partners', 'Distributors', 'Local operators', 'Industry experts', 'Legal advisors', 'Tax advisors', 'Hiring partners'].map((intro) => (
                                                    <label key={intro} className="gep-checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.introductionsNeeded.includes(intro)}
                                                            onChange={() => toggleArrayItem('introductionsNeeded', intro)}
                                                        />
                                                        <span>{intro}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>First 90-day outcome</label>
                                            <textarea
                                                rows={3}
                                                placeholder="What measurable result would make this expansion support valuable?"
                                                value={formData.first90DayOutcome}
                                                onChange={(e) => handleChange('first90DayOutcome', e.target.value)}
                                            ></textarea>
                                            {errors.first90DayOutcome && <span className="gep-err">{errors.first90DayOutcome}</span>}
                                        </div>

                                        <div className="gep-field full-width">
                                            <label>Anything else we should know?</label>
                                            <textarea
                                                rows={2}
                                                placeholder="Share partner history, failed attempts, deadlines, sensitivities or special requirements."
                                                value={formData.anythingElse}
                                                onChange={(e) => handleChange('anythingElse', e.target.value)}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="gep-checkbox-row">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.supportAcknowledged}
                                                onChange={(e) => handleChange('supportAcknowledged', e.target.checked)}
                                            />
                                            <span>I understand that Markoknow facilitates research, planning and relevant introductions but does not guarantee commercial outcomes.</span>
                                        </label>
                                        {errors.supportAcknowledged && <span className="gep-err">{errors.supportAcknowledged}</span>}
                                    </div>
                                </div>
                            )}

                            {/* STEP 5: REVIEW EXPANSION PLAN */}
                            {currentStep === 5 && (
                                <div className="gep-step-body">
                                    <div className="gep-step-title-group">
                                        <h2>Expansion plan review</h2>
                                        <p>Check that every section reflects your current position</p>
                                    </div>

                                    {/* 4 Summary Cards */}
                                    <div className="gep-review-summary-grid">
                                        <div className="gep-rev-box">
                                            <div className="gep-rb-top">
                                                <div className="gep-rb-title">
                                                    <span>🏢</span>
                                                    <strong>Company</strong>
                                                </div>
                                                <button type="button" onClick={() => setCurrentStep(1)}>Edit &gt;</button>
                                            </div>
                                            <h4>{formData.companyName || 'Company Name'} · {formData.sector || 'Sector'} · {formData.stage || 'Stage'}</h4>
                                            <p>{formData.headquarters ? `${formData.headquarters} · ` : ''}{formData.currency || 'USD'} {parseInt(formData.homeMarketRevenue, 10) ? parseInt(formData.homeMarketRevenue, 10).toLocaleString() : '0'} annual revenue</p>
                                        </div>

                                        <div className="gep-rev-box">
                                            <div className="gep-rb-top">
                                                <div className="gep-rb-title">
                                                    <span>🌐</span>
                                                    <strong>Target markets</strong>
                                                </div>
                                                <button type="button" onClick={() => setCurrentStep(2)}>Edit &gt;</button>
                                            </div>
                                            <h4>Priority: {formData.priorityMarket || 'Not specified'}</h4>
                                            <p>Also considering: {formData.additionalMarkets && formData.additionalMarkets.length > 0 ? formData.additionalMarkets.join(', ') : 'None'}</p>
                                        </div>

                                        <div className="gep-rev-box">
                                            <div className="gep-rb-top">
                                                <div className="gep-rb-title">
                                                    <span>📊</span>
                                                    <strong>Readiness</strong>
                                                </div>
                                                <button type="button" onClick={() => setCurrentStep(3)}>Edit &gt;</button>
                                            </div>
                                            <h4>Current score: 62%</h4>
                                            <p>Strong evidence · Compliance and economics to test</p>
                                        </div>

                                        <div className="gep-rev-box">
                                            <div className="gep-rb-top">
                                                <div className="gep-rb-title">
                                                    <span>👥</span>
                                                    <strong>Support required</strong>
                                                </div>
                                                <button type="button" onClick={() => setCurrentStep(4)}>Edit &gt;</button>
                                            </div>
                                            <h4>{formData.supportWorkstreams && formData.supportWorkstreams.length > 0 ? formData.supportWorkstreams.slice(0, 3).join(' · ') : 'None selected'}</h4>
                                            <p>Preferred format: {formData.engagementFormat || 'Not selected'}</p>
                                        </div>
                                    </div>

                                    {/* Proposed First Outcome Banner */}
                                    <div className="gep-proposed-outcome-box">
                                        <span className="gep-po-icon">🎯</span>
                                        <p>{formData.first90DayOutcome || 'No specific outcome entered.'}</p>
                                    </div>

                                    {/* Supporting Documents */}
                                    <div className="gep-supporting-docs">
                                        <label>Supporting documents & materials</label>
                                        <div className="gep-docs-row">
                                            {formData.documents && formData.documents.map((doc, idx) => (
                                                <div key={idx} className="gep-doc-chip uploaded">
                                                    <span>{doc.name.toLowerCase().endsWith('.pdf') ? '📊' : '📑'} {doc.name}</span>
                                                    <span className="gep-badge-green">{doc.isPending ? 'READY' : doc.size || 'UPLOADED'}</span>
                                                    <button
                                                        type="button"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', marginLeft: 4, fontWeight: 'bold' }}
                                                        onClick={() => handleRemoveDoc(idx)}
                                                        disabled={loading}
                                                        title="Remove document"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}

                                            {formData.dataRoomLink && (
                                                <div className="gep-doc-chip uploaded">
                                                    <span>🔒 Data room</span>
                                                    <span className="gep-badge-green">LINKED</span>
                                                    <button
                                                        type="button"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', marginLeft: 4, fontWeight: 'bold' }}
                                                        onClick={() => setFormData((prev) => ({ ...prev, dataRoomLink: '' }))}
                                                        disabled={loading}
                                                        title="Remove link"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            )}

                                            <label className="gep-btn-add-doc" style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
                                                <input
                                                    type="file"
                                                    multiple
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileSelection(e, 'Market Research')}
                                                    disabled={loading}
                                                    accept=".pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg"
                                                />
                                                + Add document
                                            </label>

                                            {!formData.dataRoomLink && !showDataRoomInput && (
                                                <button
                                                    type="button"
                                                    className="gep-btn-add-doc"
                                                    style={{ borderStyle: 'dotted' }}
                                                    onClick={() => setShowDataRoomInput(true)}
                                                    disabled={loading}
                                                >
                                                    <span>🔒 + Add Data Room link</span>
                                                </button>
                                            )}
                                        </div>

                                        {showDataRoomInput && (
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                                <input
                                                    type="text"
                                                    placeholder="Paste data room link (e.g. DocSend, Google Drive, Notion)..."
                                                    value={tempDataRoomLink}
                                                    onChange={(e) => setTempDataRoomLink(e.target.value)}
                                                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                                                />
                                                <button
                                                    type="button"
                                                    className="gep-btn-signup"
                                                    style={{ padding: '8px 16px', fontSize: 12 }}
                                                    onClick={() => {
                                                        if (tempDataRoomLink.trim()) {
                                                            setFormData((prev) => ({ ...prev, dataRoomLink: tempDataRoomLink.trim() }));
                                                            setTempDataRoomLink('');
                                                            setShowDataRoomInput(false);
                                                        }
                                                    }}
                                                >
                                                    Add Link
                                                </button>
                                                <button
                                                    type="button"
                                                    className="gep-btn-link"
                                                    onClick={() => {
                                                        setTempDataRoomLink('');
                                                        setShowDataRoomInput(false);
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                        {errors.dataRoomLink && <span className="gep-err" style={{ display: 'block', marginBottom: 12 }}>{errors.dataRoomLink}</span>}
                                    </div>

                                    {/* Declarations */}
                                    <div className="gep-declarations">
                                        <label>Declarations</label>
                                        <div className="gep-checkbox-row">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.declarationAccuracy}
                                                    onChange={(e) => handleChange('declarationAccuracy', e.target.checked)}
                                                />
                                                <span>I confirm that the information provided is accurate and current.</span>
                                            </label>
                                        </div>
                                        <div className="gep-checkbox-row">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.declarationNoGuarantee}
                                                    onChange={(e) => handleChange('declarationNoGuarantee', e.target.checked)}
                                                />
                                                <span>I understand that submission does not guarantee market-entry success, partnerships or commercial outcomes.</span>
                                            </label>
                                        </div>
                                        {errors.declarations && <span className="gep-err">{errors.declarations}</span>}
                                    </div>
                                </div>
                            )}

                            {errorMsg && (
                                <div style={{ padding: '12px 16px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, margin: '16px 0' }}>
                                    ⚠️ {errorMsg}
                                </div>
                            )}

                            {/* Bottom Actions Row */}
                            <div className="gep-actions-row">
                                {currentStep > 1 && (
                                    <button type="button" className="gep-btn-back" onClick={handleBack} disabled={loading}>
                                        ← Back
                                    </button>
                                )}

                                <div className="gep-actions-right">
                                    <button type="button" className="gep-btn-link" onClick={() => alert('Draft saved locally.')} disabled={loading}>
                                        Save and continue later
                                    </button>
                                    <button type="submit" className="gep-btn-submit" disabled={loading} style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                                        {loading ? (submittingStatus || 'Submitting...') : (
                                            <>
                                                {currentStep === 1 && 'Continue to target markets →'}
                                                {currentStep === 2 && 'Continue to readiness →'}
                                                {currentStep === 3 && 'Continue to support needs →'}
                                                {currentStep === 4 && 'Continue to review →'}
                                                {currentStep === 5 && 'Submit expansion plan →'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="gep-footer-note">
                                {currentStep === 1 && "Submitting a plan does not guarantee market-entry success, partnerships or commercial results."}
                                {currentStep === 2 && "You can compare several markets before selecting one for deeper validation."}
                                {currentStep === 3 && "Readiness gaps do not disqualify a plan; they help define the correct sequence."}
                                {currentStep === 4 && "Support scope and commercial terms are confirmed only after the expansion review."}
                                {currentStep === 5 && "You may update the plan if material information changes before review."}
                            </div>
                        </form>
                    </section>
                )}

                {/* =========================================================================
            RIGHT DARK CONTEXT SIDEBAR (STEPS 1 - 5)
            ========================================================================= */}
                {currentStep <= 5 && (
                    <aside className="gep-context-sidebar">
                        {/* STEP 1 SIDEBAR */}
                        {currentStep === 1 && (
                            <div className="gep-sb-panel">
                                <div className="gep-stamp">MARKET ENTRY REVIEW</div>
                                <div className="gep-sb-card">
                                    <span className="gep-sb-subtag">EXPANSION READINESS</span>
                                    <h3>Start with evidence.</h3>
                                    <p>Strong expansion plans connect proven demand at home with a clear reason to enter a new market.</p>
                                    <ul className="gep-sb-checklist">
                                        <li><span className="gep-ch-dot">✓</span> Home-market traction</li>
                                        <li><span className="gep-ch-dot">✓</span> Repeatable business model</li>
                                        <li><span className="gep-ch-dot">✓</span> Leadership capacity</li>
                                        <li><span className="gep-ch-dot">✓</span> Clear expansion objective</li>
                                    </ul>
                                </div>
                                <div className="gep-sb-meta-grid">
                                    <div>
                                        <span className="gep-meta-val">01 / 05</span>
                                        <span className="gep-meta-lbl">PLANNING STEP</span>
                                    </div>
                                    <div>
                                        <span className="gep-meta-val">10–12 MIN</span>
                                        <span className="gep-meta-lbl">ESTIMATED TIME</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 2 SIDEBAR */}
                        {currentStep === 2 && (
                            <div className="gep-sb-panel">
                                <div className="gep-stamp">MARKET THESIS</div>
                                <div className="gep-sb-card">
                                    <span className="gep-sb-subtag">MARKET SELECTION</span>
                                    <h3>Interest is not evidence.</h3>
                                    <p>A useful market thesis connects demand, access, economics and execution difficulty.</p>
                                    <div className="gep-sb-bars">
                                        <div className="gep-bar-row">
                                            <span>Demand</span>
                                            <div className="gep-mini-track"><div className="gep-mini-fill" style={{ width: '85%' }}></div></div>
                                            <strong>STRONG</strong>
                                        </div>
                                        <div className="gep-bar-row">
                                            <span>Access</span>
                                            <div className="gep-mini-track"><div className="gep-mini-fill" style={{ width: '70%' }}></div></div>
                                            <strong>OPEN</strong>
                                        </div>
                                        <div className="gep-bar-row">
                                            <span>Economics</span>
                                            <div className="gep-mini-track"><div className="gep-mini-fill" style={{ width: '45%' }}></div></div>
                                            <strong>TO TEST</strong>
                                        </div>
                                        <div className="gep-bar-row">
                                            <span>Entry friction</span>
                                            <div className="gep-mini-track"><div className="gep-mini-fill" style={{ width: '60%' }}></div></div>
                                            <strong>MEDIUM</strong>
                                        </div>
                                    </div>
                                </div>
                                <div className="gep-sb-meta-grid">
                                    <div>
                                        <span className="gep-meta-val">02 / 05</span>
                                        <span className="gep-meta-lbl">PLANNING STEP</span>
                                    </div>
                                    <div>
                                        <span className="gep-meta-val">40%</span>
                                        <span className="gep-meta-lbl">PLAN COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 3 SIDEBAR */}
                        {currentStep === 3 && (
                            <div className="gep-sb-panel">
                                <div className="gep-stamp">READINESS CHECK</div>
                                <div className="gep-sb-card">
                                    <span className="gep-sb-subtag">ENTRY READINESS</span>
                                    <h3>Sequence before speed.</h3>
                                    <p>Market entry becomes expensive when teams commit before validating the critical dependencies.</p>
                                    <div className="gep-gauge-box">
                                        <div className="gep-circular-gauge">
                                            <span>62%</span>
                                            <small>CURRENT READINESS</small>
                                        </div>
                                        <div className="gep-gauge-bars">
                                            <div className="gep-bar-row">
                                                <span>Market evidence</span>
                                                <strong>STRONG</strong>
                                            </div>
                                            <div className="gep-bar-row">
                                                <span>Team capacity</span>
                                                <strong>PARTIAL</strong>
                                            </div>
                                            <div className="gep-bar-row">
                                                <span>Local compliance</span>
                                                <strong>TO REVIEW</strong>
                                            </div>
                                            <div className="gep-bar-row">
                                                <span>Entry economics</span>
                                                <strong>TO TEST</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="gep-sb-meta-grid">
                                    <div>
                                        <span className="gep-meta-val">03 / 05</span>
                                        <span className="gep-meta-lbl">PLANNING STEP</span>
                                    </div>
                                    <div>
                                        <span className="gep-meta-val">60%</span>
                                        <span className="gep-meta-lbl">PLAN COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 4 SIDEBAR */}
                        {currentStep === 4 && (
                            <div className="gep-sb-panel">
                                <div className="gep-stamp">LOCAL CONTEXT</div>
                                <div className="gep-sb-card">
                                    <span className="gep-sb-subtag">SUPPORT DESIGN</span>
                                    <h3>Use help where it compounds.</h3>
                                    <p>The right engagement closes the most important gaps without replacing your team's ownership.</p>
                                    <div className="gep-pipeline-steps">
                                        <div className="gep-ps-item">
                                            <div className="gep-ps-num">01</div>
                                            <strong>VALIDATE</strong>
                                            <span>Evidence</span>
                                        </div>
                                        <div className="gep-ps-item">
                                            <div className="gep-ps-num">02</div>
                                            <strong>DESIGN</strong>
                                            <span>Entry model</span>
                                        </div>
                                        <div className="gep-ps-item">
                                            <div className="gep-ps-num">03</div>
                                            <strong>ACTIVATE</strong>
                                            <span>Introductions</span>
                                        </div>
                                        <div className="gep-ps-item">
                                            <div className="gep-ps-num">04</div>
                                            <strong>TRACTION</strong>
                                            <span>Milestones</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="gep-sb-meta-grid">
                                    <div>
                                        <span className="gep-meta-val">04 / 05</span>
                                        <span className="gep-meta-lbl">PLANNING STEP</span>
                                    </div>
                                    <div>
                                        <span className="gep-meta-val">80%</span>
                                        <span className="gep-meta-lbl">PLAN COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 5 SIDEBAR */}
                        {currentStep === 5 && (
                            <div className="gep-sb-panel">
                                <div className="gep-stamp">READY FOR REVIEW</div>
                                <div className="gep-sb-card">
                                    <span className="gep-sb-subtag">WHAT HAPPENS NEXT</span>
                                    <h3>From plan to first traction.</h3>
                                    <p>The Global Expansion team reviews the evidence, sequencing and availability of relevant local support.</p>
                                    <div className="gep-review-flow">
                                        <div className="gep-rf-step">
                                            <div className="gep-rf-num">01</div>
                                            <div>
                                                <strong>Plan review</strong>
                                                <span>Evidence and market thesis</span>
                                            </div>
                                        </div>
                                        <div className="gep-rf-step">
                                            <div className="gep-rf-num">02</div>
                                            <div>
                                                <strong>Readiness call</strong>
                                                <span>Risks, constraints and priorities</span>
                                            </div>
                                        </div>
                                        <div className="gep-rf-step">
                                            <div className="gep-rf-num">03</div>
                                            <div>
                                                <strong>Entry recommendation</strong>
                                                <span>Market, sequence and engagement scope</span>
                                            </div>
                                        </div>
                                        <div className="gep-rf-step">
                                            <div className="gep-rf-num">04</div>
                                            <div>
                                                <strong>Activation</strong>
                                                <span>Local operators and first milestones</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="gep-response-pill">
                                        REVIEW RESPONSE · WITHIN 5–7 BUSINESS DAYS
                                    </div>
                                </div>
                                <div className="gep-sb-meta-grid">
                                    <div>
                                        <span className="gep-meta-val">05 / 05</span>
                                        <span className="gep-meta-lbl">PLANNING STEP</span>
                                    </div>
                                    <div>
                                        <span className="gep-meta-val">100%</span>
                                        <span className="gep-meta-lbl">PLAN COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="gep-sb-footer-disclaimer">
                            Legal, tax and regulatory advice is provided only by independent qualified professionals.
                        </div>
                    </aside>
                )}

                {/* =========================================================================
            STEP 6: CONFIRMATION / SUCCESS SCREEN
            ========================================================================= */}
                {currentStep === 6 && (
                    <section className="gep-success-layout">
                        <div className="gep-success-left">
                            <div className="gep-success-head">
                                <div className="gep-check-badge">✓</div>
                                <div>
                                    <span className="gep-success-tag">EXPANSION PLAN RECEIVED</span>
                                    <h1 className="gep-success-title">Your market plan is in review.</h1>
                                </div>
                            </div>

                            <p className="gep-success-desc">
                                Thank you{formData.founderName ? `, ${formData.founderName.trim().split(' ')[0]}` : ''}. Your Global Expansion plan has been submitted securely. We'll review the market thesis, readiness gaps and availability of relevant local support.
                            </p>

                            {/* Reference Box */}
                            <div className="gep-reference-box">
                                <div className="gep-ref-info">
                                    <span className="gep-ref-lbl">PLAN REFERENCE</span>
                                    <div className="gep-ref-code">
                                        <span>{submittedData?.referenceCode || 'GE-2026-1016-084'}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const code = submittedData?.referenceCode || 'GE-2026-1016-084';
                                                navigator.clipboard.writeText(code);
                                                setCopied(true);
                                                setTimeout(() => setCopied(false), 2000);
                                            }}
                                            title="Copy reference code"
                                        >
                                            {copied ? '✓' : '📋'}
                                        </button>
                                    </div>
                                    <span className="gep-ref-date">
                                        Submitted {submittedData?.createdAt ? new Date(submittedData.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Today'}
                                    </span>
                                </div>
                                <div className="gep-ref-actions">
                                    <button
                                        type="button"
                                        className="gep-btn-status"
                                        onClick={() => alert(`Your plan status: ${submittedData?.status || 'Pending Review'}`)}
                                    >
                                        View plan status →
                                    </button>
                                    <a href="#return" onClick={(e) => { e.preventDefault(); setCurrentStep(1); }}>Return to Global Expansion</a>
                                </div>
                            </div>

                            {/* What Happens Next Cards */}
                            <h3 className="gep-next-title">What happens next?</h3>
                            <div className="gep-next-cards-row">
                                <div className="gep-next-card">
                                    <div className="gep-nc-num">01</div>
                                    <div className="gep-nc-icon">📑</div>
                                    <h4>Assessment</h4>
                                    <p>We review your evidence, target market and readiness.</p>
                                </div>
                                <div className="gep-next-card">
                                    <div className="gep-nc-num">02</div>
                                    <div className="gep-nc-icon">💬</div>
                                    <h4>Expansion call</h4>
                                    <p>We clarify constraints, priorities and the first 90-day outcome.</p>
                                </div>
                                <div className="gep-next-card">
                                    <div className="gep-nc-num">03</div>
                                    <div className="gep-nc-icon">📊</div>
                                    <h4>Recommendation</h4>
                                    <p>We share the suggested market-entry sequence and support scope.</p>
                                </div>
                            </div>

                            {/* Confirmation Alert */}
                            <div className="gep-confirmation-toast">
                                <span className="gep-toast-icon">✉️</span>
                                <div>
                                    <strong>Confirmation sent</strong>
                                    <p>A copy of your plan and reference number has been recorded for <u>{formData.workEmail || 'your email'}</u>.</p>
                                </div>
                            </div>

                            <div className="gep-correction-text">
                                Need to correct something? Update your plan before assessment begins. <a href="#manage" onClick={(e) => { e.preventDefault(); setCurrentStep(5); }}>Manage submission</a>
                            </div>
                        </div>

                        {/* Step 6 Right Dark Sidebar */}
                        <aside className="gep-context-sidebar">
                            <div className="gep-sb-panel">
                                <div className="gep-stamp">SUBMITTED SECURELY</div>
                                <div className="gep-sb-card">
                                    <span className="gep-sb-subtag">GLOBAL EXPANSION</span>
                                    <h3>What happens now?</h3>
                                    <div className="gep-vertical-timeline">
                                        <div className="gep-vt-item completed">
                                            <div className="gep-vt-dot">✓</div>
                                            <div>
                                                <strong>Today</strong>
                                                <p>Plan received</p>
                                            </div>
                                        </div>
                                        <div className="gep-vt-item">
                                            <div className="gep-vt-dot"></div>
                                            <div>
                                                <strong>1–3 business days</strong>
                                                <p>Initial expansion assessment</p>
                                            </div>
                                        </div>
                                        <div className="gep-vt-item">
                                            <div className="gep-vt-dot"></div>
                                            <div>
                                                <strong>5–7 business days</strong>
                                                <p>Review response</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="gep-status-stats">
                                        <div>
                                            <span className="gep-ss-lbl">PLAN STATUS</span>
                                            <strong className="gep-ss-val">{(submittedData?.status || 'RECEIVED').toUpperCase()}</strong>
                                        </div>
                                        <div>
                                            <span className="gep-ss-lbl">DOCUMENTS</span>
                                            <strong className="gep-ss-val">📑 {submittedData?.documents?.length || formData.documents?.length || 0} UPLOADED</strong>
                                        </div>
                                    </div>

                                    <div className="gep-security-note">
                                        🛡️ No market introduction or external sharing occurs without relevance checks and your confirmation.
                                    </div>

                                    <div className="gep-reference-mini">
                                        <span>Reference:</span>
                                        <strong>{submittedData?.referenceCode || 'GE-2026-1016-084'}</strong>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </section>
                )}
            </main>
        </div>
    );
};

export default GlobalExpansionPlanner;